/* eslint-disable upright/no-primer-heading */
import { Heading as PrimerHeading } from '@primer/react';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import styled, { css } from 'styled-components';
import { HeadingProps, HeadingSize } from './types';

/* CUSTOM HEADING – USE FOR PAGE AND SECTION HEADINGS ON THE PLATFORM

DIRECTIONS
1. HTML TAG: Provide the correct html tag (h1-h6) through the as prop. Renders h2 by default.
    Please reference the w3 recommendations for headings: https://www.w3.org/WAI/tutorials/page-structure/headings/

2. SIZING: Sizing is responsive. You can provide a fontSize prop which overrides the styled-system's prop with same name (4 options outside the default size):
    default (i.e. medium):
    Default page title. Great for page header composition.

    small:
    Secondary title. Great for things underneath page header and elements where real estate is limited.

    micro:
    For microcopy, overtitles, etc. Remains same size across devices. 

    large:
    For rare cases when the default option isn't sufficiently large

    display:
    Hero-style text ONLY for brand / marketing pages

    Err on the side of smaller – default, small and micro should suffice.

3. COLOR: If you define a color, only use the available text or foreground colors in the theme.
   Example: <Heading sx={{ color: 'fg.muted' }}>
*/

/**
 * @deprecated Use UprightHeading instead
 */
export const Heading = (props: HeadingProps) => {
  return (
    <StyledHeading
      className={props.className}
      forwardedAs={props.as}
      fontSize={props.fontSize}
      sx={{
        color: 'fg.default',
        ...props.sx,
      }}
    >
      {props.children}
    </StyledHeading>
  );
};
const StyledHeading = styled(PrimerHeading).attrs(props => ({
  sx: props.sx,
}))<{ fontSize?: HeadingSize }>`
  font-family: ${themeGet('fonts.heading')};
  text-transform: uppercase;
  font-kerning: normal;
  -webkit-font-kerning: normal;
  line-height: ${themeGet('lineHeights.condensed')};
  ${handleResponsiveHeadingSize};
`;

function handleResponsiveHeadingSize({ fontSize = 'default' }: { fontSize?: HeadingSize }) {
  switch (fontSize) {
    case 'micro':
      return css`
        font-size: ${themeGet('fontSizes.0')}; // 12px. Remains the same size on both mobile & desktop.
      `;
    case 'small':
      return css`
        font-size: ${themeGet('fontSizes.2')}; // 16px. Remains the same size on both mobile & desktop.
      `;
    case 'default':
      return css`
        font-size: ${themeGet('fontSizes.3')}; // 20px
        @media screen and (min-width: ${themeGet('breakpoints.1')}) {
          font-size: ${themeGet('fontSizes.4')}; // 24px at 768px
        }
        @media screen and (min-width: 1920px) {
          font-size: ${themeGet('fontSizes.5')}; // 32px at 1920px
        }
      `;
    case 'large':
      return css`
        font-size: ${themeGet('fontSizes.4')}; // 24px
        @media screen and (min-width: ${themeGet('breakpoints.1')}) {
          font-size: ${themeGet('fontSizes.5')}; // 32px at 768px
        }
        @media screen and (min-width: ${themeGet('breakpoints.3')}) {
          font-size: ${themeGet('fontSizes.6')}; // 40px at 1280px
        }
      `;
    case 'display':
      return css`
        font-size: ${themeGet('fontSizes.4')}; // 24px
        @media screen and (min-width: ${themeGet('breakpoints.1')}) {
          font-size: ${themeGet('fontSizes.5')}; // 32px at 768px
        }
        @media screen and (min-width: ${themeGet('breakpoints.3')}) {
          font-size: ${themeGet('fontSizes.6')}; // 40px at 1280px
        }
        @media screen and (min-width: 1920px) {
          font-size: ${themeGet('fontSizes.7')}; // 48px at 1920px
        }
      `;
    default:
      // Will not execute
      throw Error('Heading size out of scope');
  }
}
